import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreatePayDataMutation,
  useLazyCheckXnttNoExistsQuery,
  useLazyGetPayDataQuery,
  useLazyPayDataBankAccountLookupQuery,
  useLazyPayDataBankLookupQuery,
  useLazyPayDataContractInfoQuery,
  useLazyPayDataContractsLookupQuery,
  useLazyPayDataInvoiceInfoQuery,
  useLazyPayDataInvoiceNoLookupQuery,
  useLazyPayDataProvisionalCheckQuery,
  useLazyPayDataTruckIdLookupQuery,
  useLazySuggestXnttNoQuery,
  usePayDataPaymentTypeLookupQuery,
  usePayDataPriceUnitsLookupQuery,
  useUpdatePayDataMutation,
} from '@/store/api/procurementApi'
import type { PayDataDetail, PayDataLookupItem, PayDataWrite } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from '../purchase/dialogStyle'
import ProcurementInvoiceFormDialog from './ProcurementInvoiceFormDialog'

// ---------------------------------------------------------------------------
// Props — exact signature for orchestrator wiring.
// ---------------------------------------------------------------------------
interface Props {
  open: boolean
  mode: 'new' | 'edit' | 'copy'
  paydataId: number | null
  canEdit: boolean
  /** Prefill hợp đồng khi mở mode New (Access: Contract Detail Tab2 btnNew /
   *  Tab3 btnAddPay mở form với contract đã chọn sẵn + cascade prefill). */
  initialContractNo?: string | null
  onClose: () => void
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Draft state — every editable + display field. Numbers stored as formatted
// strings ("1,234.56") so the inputs can show thousand separators.
// ---------------------------------------------------------------------------
interface DraftState {
  // contract routing
  p_contract: string
  consignment_contract: string
  // identity / header
  payment_date: string
  xntt_no: string
  truck_id: string
  payment_type: string
  // banking
  bank: string
  bank_account: string
  bank_sub: string
  bank_account_sub: string
  // amounts
  quantity: string
  price: string
  price_unit: string // Request PriceUnit (Access request_price_unit → cột price_unit)
  futures: string
  lot: string
  advanced: string
  requested_usd: string
  requested_fx: string
  requested_vnd: string
  paid_usd: string
  fx: string
  paid_vnd: string
  vat_amount_usd: string // Access Visible=NotDefault — giữ payload, KHÔNG render
  vat_amount_vnd: string
  interest_rate: string
  fixation_deadline: string
  invoice_no: string
  note: string
  // sub
  advanced_sub: string
  requested_usd_sub: string
  requested_vnd_sub: string
  invoice_no_sub: string
  // display-only (not saved) — Access RO snapshot fields
  supplier: string
  shipper_code: string
  contract_ref_sub: string
  price_sub: string
  contract_price_unit: string // Access `price_unit` (Contract - Price Unit, RO)
  vat_txt: string // Access `vat_txt` (Applicable VAT?, RO DLookUp mv_local_p)
  npe_fix_no: string // Access `npe_fix_no` (NPE Fix.No, RO DLookUp v_cs_fixation_p)
}

const EMPTY_DRAFT: DraftState = {
  p_contract: '',
  consignment_contract: '',
  payment_date: '',
  xntt_no: '',
  truck_id: '',
  payment_type: '',
  bank: '',
  bank_account: '',
  bank_sub: '',
  bank_account_sub: '',
  quantity: '',
  price: '',
  price_unit: '',
  futures: '',
  lot: '',
  advanced: '',
  requested_usd: '',
  requested_fx: '',
  requested_vnd: '',
  paid_usd: '',
  fx: '',
  paid_vnd: '',
  vat_amount_usd: '',
  vat_amount_vnd: '',
  interest_rate: '',
  fixation_deadline: '',
  invoice_no: '',
  note: '',
  advanced_sub: '',
  requested_usd_sub: '',
  requested_vnd_sub: '',
  invoice_no_sub: '',
  supplier: '',
  shipper_code: '',
  contract_ref_sub: '',
  price_sub: '',
  contract_price_unit: '',
  vat_txt: '',
  npe_fix_no: '',
}

// ---------------------------------------------------------------------------
// Contract pricing snapshot — mọi DLookup Access cần cho payment_type
// repricing + Check_Amount + save validation (contract-info BE trả về).
// ---------------------------------------------------------------------------
interface ContractSnap {
  ctype: string
  vatRate: number
  outrightPrice: number | null
  ptbfDiffs: number | null
  proviMarketRate: number | null
  pending: number | null
  avgPrice2: number | null
  lotFixed: number | null
  avgFpPaydata: number | null
  avgFpRealPaydata: number | null
  avgFpRealPaydataProvi: number | null
  priceAfterInterest: number | null
  npeRef: string
  allocatedAmount: number | null
  hasFixation: boolean
  toBeAdvance: number | null
  // Access xntt-suggest theo v_cs_fixation_p (p_contract_no = HĐ hiện tại) — dùng
  // cho CẢ P lẫn NPE, KHÔNG gate theo npe (npeRef bị gate vì phục vụ invoice filter).
  fixSuggestContract: string
  fixAllocated: number | null
}

const EMPTY_SNAP: ContractSnap = {
  ctype: '',
  vatRate: 0,
  outrightPrice: null,
  ptbfDiffs: null,
  proviMarketRate: null,
  pending: null,
  avgPrice2: null,
  lotFixed: null,
  avgFpPaydata: null,
  avgFpRealPaydata: null,
  avgFpRealPaydataProvi: null,
  priceAfterInterest: null,
  npeRef: '',
  allocatedAmount: null,
  hasFixation: false,
  toBeAdvance: null,
  fixSuggestContract: '',
  fixAllocated: null,
}

// ---------------------------------------------------------------------------
// Numeric helpers (Access Format = Standard → thousand sep + 2dp).
// ---------------------------------------------------------------------------
function parseNumeric(v: string | number | null | undefined): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** requested_usd giữ 3 decimals như Access (Round(...,3)) — display min 2dp. */
function formatUsd3(v: number | null | undefined): string {
  if (v == null) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 })
}

/**
 * Excel ROUND() — round half AWAY FROM ZERO (khác Math.round half-up ở số âm).
 * LƯU Ý: `Round()` trong Check_Amount của Access là VBA intrinsic = banker's
 * (nửa về chẵn) — KHÔNG có custom Round override (module chỉ có RoundNormal→
 * Postgres round_up, form PayData không dùng). Team CHỦ ĐỘNG chọn Excel round
 * (away-from-zero) thay cho banker's; chỉ lệch tại đúng biên .5 (rất hiếm vì số
 * là tích của float). Đổi lại đây nếu sau này cần khớp tuyệt đối VBA.
 */
const excelRound = (v: number, digits = 0): number => {
  if (!Number.isFinite(v)) return 0
  const f = 10 ** digits
  return (Math.sign(v) * Math.round(Math.abs(v) * f)) / f
}
const round2 = (v: number): number => excelRound(v, 2)
const round3 = (v: number): number => excelRound(v, 3)
const num0 = (v: string | number | null | undefined): number => parseNumeric(v) ?? 0

function buildSnap(info: Record<string, unknown>, npe: boolean): ContractSnap {
  return {
    ctype: npe ? '' : String(info.contract_type ?? ''),
    vatRate: num0(info.vat_rate as never),
    outrightPrice: parseNumeric(info.outright_price as never),
    ptbfDiffs: parseNumeric(info.ptbf_diffs as never),
    proviMarketRate: parseNumeric(info.provi_market_rate as never),
    pending: parseNumeric(info.pending as never),
    avgPrice2: parseNumeric(info.avg_price2 as never),
    lotFixed: parseNumeric(info.lot_fixed as never),
    avgFpPaydata: parseNumeric(info.avg_futures_price_paydata as never),
    avgFpRealPaydata: parseNumeric(info.avg_futures_price_real_paydata as never),
    avgFpRealPaydataProvi: parseNumeric(info.avg_futures_price_real_paydata_provi as never),
    priceAfterInterest: parseNumeric(info.price_after_interest as never),
    npeRef: npe ? '' : String(info.npe_ref ?? ''),
    allocatedAmount: parseNumeric(info.allocated_amount as never),
    hasFixation: !npe && String(info.npe_fix_no ?? '').trim() !== '',
    toBeAdvance: parseNumeric(info.to_be_advance as never),
    fixSuggestContract: String(info.fix_suggest_contract ?? ''),
    fixAllocated: parseNumeric(info.fix_allocated_amount as never),
  }
}

/** Build the PK string fed to suggest/check (Access keys xntt_no by contract). */
function activeContract(d: DraftState): string {
  return (d.p_contract || d.consignment_contract || '').trim()
}

/** A contract_no starting with NPE → consignment/advanced; "P" → p_contract. */
function isNpe(contractNo: string | null | undefined): boolean {
  return String(contractNo ?? '').toUpperCase().startsWith('NPE')
}

type Opt = PayDataLookupItem

/** Grid cell placement (Access Left→column, Top-rank→row). */
const cell = (col: 1 | 2, row: number): SxProps<Theme> => ({
  gridColumn: { xs: '1', sm: String(col) },
  gridRow: { sm: String(row) },
})

const sectionSx = (row: number): SxProps<Theme> => ({
  gridColumn: '1 / -1',
  gridRow: { sm: String(row) },
  mt: 0.5,
  color: 'text.secondary',
  ...sectionHeaderSx,
})

/**
 * PayData New / Edit / Copy dialog — converts Access form
 * `frmProcurementQryPayDataAdd` (Procurement - PayData, btnNew/btnEdit/btnCopy).
 *
 * Layout is 1:1 with the Access Left/Top grid (2-col, explicit gridRow per
 * field; mobile stacks to 1 column). Sections mirror the Access labels:
 * General Information / Request / Actual Payment / For NPE + Invoice
 * Information / Sub Information.
 *
 * Combo cascades mirror the Access VBA:
 * - p_contract_AfterUpdate: supplier/sub snapshot, clear payment_type, quantity
 *   = pending, price = outright_price|avg_price2 (fixation → price_after_interest
 *   + VND/KG), suggest xntt_no (fixation-aware), reload bank/truck/invoice combos.
 * - consignment_contract_AfterUpdate: NPE forces payment_type=Advanced (disabled),
 *   PriceUnit=VND/KG, price cleared, truck hidden.
 * - payment_type_AfterUpdate: reprice theo Outright/Advanced/Fixation/Provisional.
 * - bank/bank_sub_AfterUpdate: reload account combo (bank_sub theo shipper_code)
 *   + auto-select first account (ItemData(0)).
 * - Check_Amount: recompute requested_usd/vnd + vat theo price/quantity/fx/
 *   advanced/PriceUnit (x_to_mt) — trigger từ các AfterUpdate như Access.
 */
export default function PayDataFormDialog({
  open,
  mode,
  paydataId,
  canEdit,
  initialContractNo = null,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [contractSnap, setContractSnap] = useState<ContractSnap>(EMPTY_SNAP)
  const [hydrating, setHydrating] = useState(mode !== 'new')
  // Edit: quantity gốc của row (NPE check Quantity <= to_be_advance + qty cũ).
  const [originalQty, setOriginalQty] = useState<number>(0)
  // Provisional snapshot theo contract+truck (Access v_paydata_p_checkprovisional).
  const [provSnap, setProvSnap] = useState<Record<string, unknown> | null>(null)
  // Invoice Information RO snapshot (Access DLookUp public_invoice theo invoice_no).
  const [invoiceInfo, setInvoiceInfo] = useState<{ date: string; amount: string; vat: string }>({
    date: '',
    amount: '',
    vat: '',
  })
  // Nested Invoice Add (Access btnAddInvoice → frmProcurementQryInvoiceAdd).
  const [addInvoiceOpen, setAddInvoiceOpen] = useState(false)
  // Required-field "touched" tracking → chỉ hiện error border sau khi user rời
  // field (skill §Required: error={touched && empty}, KHÔNG flag đỏ lúc mở form).
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const touch = (key: string) => setTouched((t) => (t[key] ? t : { ...t, [key]: true }))

  // Mutations
  const [createPayData, { isLoading: creating }] = useCreatePayDataMutation()
  const [updatePayData, { isLoading: updating }] = useUpdatePayDataMutation()
  const saving = creating || updating

  // Lazy detail (Edit / Copy hydrate)
  const [fetchDetail] = useLazyGetPayDataQuery()

  // Lookups — bank/bank_account có 2 bộ instance: supplier (Request) và
  // shipper (Sub Information) vì Access filter cp_code khác nhau.
  const [fetchContracts, contractsState] = useLazyPayDataContractsLookupQuery()
  const [fetchContractInfo] = useLazyPayDataContractInfoQuery()
  const [fetchBanks, banksState] = useLazyPayDataBankLookupQuery()
  const [fetchBankAccounts, bankAccountsState] = useLazyPayDataBankAccountLookupQuery()
  const [fetchBanksSub, banksSubState] = useLazyPayDataBankLookupQuery()
  const [fetchBankAccountsSub, bankAccountsSubState] = useLazyPayDataBankAccountLookupQuery()
  const [fetchTruckIds, truckIdsState] = useLazyPayDataTruckIdLookupQuery()
  const [fetchInvoiceNos, invoiceNosState] = useLazyPayDataInvoiceNoLookupQuery()
  const [fetchInvoiceInfo] = useLazyPayDataInvoiceInfoQuery()
  const [fetchProvisional] = useLazyPayDataProvisionalCheckQuery()
  const { data: paymentTypeData } = usePayDataPaymentTypeLookupQuery()
  const { data: priceUnitsData } = usePayDataPriceUnitsLookupQuery()
  const [suggestXntt] = useLazySuggestXnttNoQuery()
  const [checkXntt] = useLazyCheckXnttNoExistsQuery()

  // Which contract combo the user is actively typing in (server-side search).
  const [pQuery, setPQuery] = useState('')
  const [npeQuery, setNpeQuery] = useState('')

  // ----- combo options -----
  const pContractOptions = useMemo<Opt[]>(() => contractsState.data?.items ?? [], [contractsState.data])
  const npeContractOptions = useMemo<Opt[]>(() => contractsState.data?.items ?? [], [contractsState.data])
  const paymentTypeOptions = useMemo<Opt[]>(() => paymentTypeData?.items ?? [], [paymentTypeData])
  const priceUnitOptions = useMemo<Opt[]>(
    () =>
      (Array.isArray(priceUnitsData) ? priceUnitsData : []).map((r) => ({
        value: r.price_unit,
        label: r.price_unit,
      })),
    [priceUnitsData],
  )
  /** Access Check_Amount: hệ số x_to_mt theo price_unit (public_price_unit). */
  const xToMt = useMemo<Record<string, number>>(
    () =>
      Object.fromEntries(
        (Array.isArray(priceUnitsData) ? priceUnitsData : []).map((r) => [
          r.price_unit,
          num0(r.x_to_mt),
        ]),
      ),
    [priceUnitsData],
  )
  const bankOptions = useMemo<Opt[]>(() => banksState.data?.items ?? [], [banksState.data])
  const bankAccountOptions = useMemo<Opt[]>(
    () => bankAccountsState.data?.items ?? [],
    [bankAccountsState.data],
  )
  const bankSubOptions = useMemo<Opt[]>(
    () => (draft.shipper_code.trim() ? banksSubState.data?.items ?? [] : []),
    [banksSubState.data, draft.shipper_code],
  )
  const bankAccountSubOptions = useMemo<Opt[]>(
    () => (draft.shipper_code.trim() ? bankAccountsSubState.data?.items ?? [] : []),
    [bankAccountsSubState.data, draft.shipper_code],
  )
  const truckIdOptions = useMemo<Opt[]>(() => truckIdsState.data?.items ?? [], [truckIdsState.data])
  const invoiceNoOptions = useMemo<Opt[]>(
    () => invoiceNosState.data?.items ?? [],
    [invoiceNosState.data],
  )

  const npeMode = !!draft.consignment_contract && !draft.p_contract

  // -------------------------------------------------------------------------
  // Check_Amount — Access engine: quy đổi price theo PriceUnit (x_to_mt) →
  // requested_usd/vnd (trừ advanced) → vat_amount theo vat_rate. Provisional +
  // truck khớp v_paydata_p_checkprovisional → auto-fill futures/fx/price trước.
  // -------------------------------------------------------------------------
  const recalcDraft = (
    prev: DraftState,
    snap: ContractSnap,
    prov: Record<string, unknown> | null,
  ): DraftState => {
    const next = { ...prev }
    const vatRate = prev.p_contract ? snap.vatRate : 0

    // Provisional auto-fill (Access: DCount checkprovisional <> 0 AND
    // payment_type = Provisional AND p_contract combo enabled → New/Copy only).
    if (prov && Object.keys(prov).length > 0 && next.payment_type === 'Provisional' && !isEdit) {
      next.futures = formatStandard(prov.futures as number | string | null)
      const pfx = parseNumeric(prov.requested_fx as number | string | null)
      if (pfx != null && pfx !== 0) next.requested_fx = formatStandard(pfx)
      next.price = formatStandard(prov.price as number | string | null)
      next.contract_price_unit = String(prov.price_unit ?? '')
    }

    const unit = next.price_unit
    let price = num0(next.price)
    if (unit !== 'VND/KG') price = round2(price)
    const fxRate = num0(next.requested_fx)
    const qty = num0(next.quantity)
    const adv = num0(next.advanced)
    const x = xToMt[unit] ?? 0

    let quydoiUsd = 0
    let quydoiVnd = 0
    if (unit === 'USD/MT') {
      quydoiUsd = price
      quydoiVnd = excelRound((price * x * fxRate) / 1000)
    } else if (unit === 'CTS/LB') {
      quydoiUsd = excelRound(price * x)
      quydoiVnd = excelRound((price * x * fxRate) / 1000)
    } else if (unit === 'VND/KG') {
      quydoiUsd = fxRate === 0 ? 0 : excelRound((price * x) / fxRate)
      quydoiVnd = price
    } else {
      quydoiUsd = 0
      quydoiVnd = excelRound(price)
    }

    let reqUsd = 0
    let reqVnd = 0
    if (unit !== 'VND/KG') {
      reqUsd = round3((quydoiUsd * qty) / 1000) - adv
      if (fxRate === 0) reqVnd = 0
      else reqVnd = adv !== 0 ? excelRound(reqUsd * fxRate) : excelRound(quydoiVnd * qty)
    } else {
      reqVnd = quydoiVnd * qty - adv
      reqUsd = 0
    }
    const vatUsd = reqUsd * vatRate
    const vatVnd = excelRound(reqVnd * vatRate)
    reqVnd = excelRound(reqVnd)

    next.price = formatStandard(price)
    next.requested_usd = formatUsd3(reqUsd)
    next.requested_vnd = formatStandard(reqVnd)
    next.vat_amount_usd = formatUsd3(vatUsd)
    next.vat_amount_vnd = formatStandard(vatVnd)
    return next
  }

  // -------------------------------------------------------------------------
  // Hydrate on open. Deps strictly [open, mode, paydataId].
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return
    let cancelled = false

    const init = async () => {
      setProvSnap(null)
      setOriginalQty(0)
      setTouched({})
      // New: empty form, today's date. Prefill contract nếu parent truyền
      // (Contract Detail Tab2 btnNew / Tab3 btnAddPay) → chạy cascade như user chọn.
      if (mode === 'new' || paydataId == null) {
        const initCn = (initialContractNo ?? '').trim()
        const initNpe = initCn ? isNpe(initCn) : false
        setDraft({
          ...EMPTY_DRAFT,
          payment_date: new Date().toISOString().slice(0, 10),
          p_contract: initCn && !initNpe ? initCn : '',
          consignment_contract: initCn && initNpe ? initCn : '',
        })
        setContractSnap(EMPTY_SNAP)
        if (initCn) {
          void applyContractInfo(initCn, initNpe)
        } else {
          // Access Form_Load: paydata_p_id = 0 → Check_Amount (fill 0.00).
          setDraft((prev) => recalcDraft(prev, EMPTY_SNAP, null))
        }
        return
      }
      // Edit / Copy: load detail.
      setHydrating(true)
      try {
        const res = await fetchDetail(paydataId).unwrap()
        if (cancelled) return
        const row: PayDataDetail = res.data
        const cn = String(row.contract_no ?? '')
        const npe = isNpe(cn)
        setOriginalQty(num0(row.quantity))
        const next: DraftState = {
          ...EMPTY_DRAFT,
          p_contract: npe ? '' : cn,
          consignment_contract: npe ? cn : '',
          payment_date: String(row.payment_date ?? '').slice(0, 10),
          xntt_no: String(row.xntt_no ?? ''),
          truck_id: String(row.truck_id ?? ''),
          payment_type: String(row.payment_type ?? ''),
          bank: String(row.bank ?? ''),
          bank_account: String(row.bank_account ?? ''),
          bank_sub: String(row.bank_sub ?? ''),
          bank_account_sub: String(row.bank_account_sub ?? ''),
          quantity: formatStandard(row.quantity),
          price: formatStandard(row.price),
          price_unit: String(row.price_unit ?? ''),
          futures: formatStandard(row.futures),
          lot: formatStandard(row.lot),
          advanced: formatStandard(row.advanced),
          requested_usd: formatUsd3(parseNumeric(row.requested_usd)),
          requested_fx: formatStandard(row.requested_fx),
          requested_vnd: formatStandard(row.requested_vnd),
          paid_usd: formatStandard(row.paid_usd),
          fx: formatStandard(row.fx),
          paid_vnd: formatStandard(row.paid_vnd),
          vat_amount_usd: formatUsd3(parseNumeric(row.vat_amount_usd)),
          vat_amount_vnd: formatStandard(row.vat_amount_vnd),
          interest_rate: formatStandard(row.interest_rate),
          fixation_deadline: String(row.fixation_deadline ?? '').slice(0, 10),
          invoice_no: String(row.invoice_no ?? ''),
          note: String(row.note ?? ''),
          advanced_sub: formatStandard(row.advanced_sub),
          requested_usd_sub: formatStandard(row.requested_usd_sub),
          requested_vnd_sub: formatStandard(row.requested_vnd_sub),
          invoice_no_sub: String(row.invoice_no_sub ?? ''),
          supplier: String(row.supplier ?? ''),
          shipper_code: String(row.shipper_code ?? ''),
          contract_ref_sub: String(row.contract_ref_sub ?? ''),
          price_sub: formatStandard(row.price_sub),
        }
        // btnCopy: clear paydata_p_id (implicit — we never carry it), re-suggest
        // xntt_no, and DROP fields btnCopy does not set. Per the Access
        // btnCopy_Click the actual paid values (paid_usd/fx/paid_vnd) and
        // vat_amount_usd are NOT carried to a copy.
        if (mode === 'copy') {
          next.paid_usd = ''
          next.fx = ''
          next.paid_vnd = ''
          next.vat_amount_usd = ''
          if (cn) {
            try {
              const sug = await suggestXntt({ contractNo: cn }).unwrap()
              if (!cancelled) next.xntt_no = sug.suggestedNo
            } catch {
              /* leave existing xntt_no on suggest failure */
            }
          }
        }
        if (cancelled) return
        setDraft(next)
        // Resolve contract snapshot (pricing/vat/fixation cho cascade + RO fields
        // row không có: Contract - Price Unit / Applicable VAT? / NPE Fix.No).
        if (cn) {
          try {
            const info = (await fetchContractInfo({ contractNo: cn }).unwrap()) as Record<string, unknown>
            if (cancelled) return
            setContractSnap(buildSnap(info, npe))
            setDraft((prev) => ({
              ...prev,
              contract_price_unit: npe ? '' : String(info.price_unit ?? ''),
              vat_txt: npe ? '' : String(info.vat_txt ?? ''),
              npe_fix_no: npe ? '' : String(info.npe_fix_no ?? ''),
            }))
          } catch {
            if (!cancelled) setContractSnap(EMPTY_SNAP)
          }
        } else {
          setContractSnap(EMPTY_SNAP)
        }
      } catch (err) {
        if (!cancelled) snack.error(getErrorMessage(err, 'Failed to load pay data'))
      } finally {
        if (!cancelled) setHydrating(false)
      }
    }

    void init()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, paydataId])

  // -------------------------------------------------------------------------
  // Contract combo searches (server-side q). Edit/Copy disable contract combos
  // so we only fetch in New mode (and to inject the saved value as an option).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return
    void fetchContracts({ kind: 'p', q: pQuery || undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pQuery])

  useEffect(() => {
    if (!open || !npeMode) return
    void fetchContracts({ kind: 'npe', q: npeQuery || undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, npeQuery, npeMode])

  // Payment types always; banks/bank-accounts/trucks/invoices keyed by contract.
  const supplierCpCode = useMemo(() => {
    // supplier display is "<cp_code> : <name>"; first token is cp_code.
    const s = draft.supplier.split(':')[0]?.trim() ?? ''
    return s
  }, [draft.supplier])

  useEffect(() => {
    if (!open) return
    if (supplierCpCode) void fetchBanks({ cpCode: supplierCpCode })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supplierCpCode])

  useEffect(() => {
    if (!open || !supplierCpCode) return
    void fetchBankAccounts({ cpCode: supplierCpCode, bank: draft.bank || undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supplierCpCode, draft.bank])

  // Sub banking — Access filter theo SHIPPER_CODE (không phải supplier).
  useEffect(() => {
    if (!open) return
    const sc = draft.shipper_code.trim()
    if (sc) void fetchBanksSub({ cpCode: sc })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.shipper_code])

  useEffect(() => {
    if (!open) return
    const sc = draft.shipper_code.trim()
    if (sc) void fetchBankAccountsSub({ cpCode: sc, bank: draft.bank_sub || undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.shipper_code, draft.bank_sub])

  useEffect(() => {
    if (!open) return
    const cn = activeContract(draft)
    if (cn) {
      void fetchTruckIds({ contractNo: cn })
      // Access P: invoice filter contract_no = P OR npe_ref (NPE fix contract).
      void fetchInvoiceNos({ contractNo: cn, contractNo2: contractSnap.npeRef || undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.p_contract, draft.consignment_contract, contractSnap.npeRef])

  // Provisional snapshot theo contract + truck (Access Check_Amount DCount).
  useEffect(() => {
    if (!open) return
    const cn = draft.p_contract.trim()
    const tid = draft.truck_id.trim()
    if (!cn || !tid) {
      setProvSnap(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const r = (await fetchProvisional({ contractNo: cn, truckId: tid }).unwrap()) as Record<string, unknown>
        if (!cancelled) setProvSnap(Object.keys(r).length > 0 ? r : null)
      } catch {
        if (!cancelled) setProvSnap(null)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.p_contract, draft.truck_id])

  // Auto-select bank account đầu tiên sau khi đổi Bank (Access bank_AfterUpdate:
  // `Me.bank_account_number.value = ItemData(0)`). Flag chỉ bật từ handlePickBank
  // để KHÔNG auto-fill khi options load vì lý do khác (Edit hydrate, contract pick).
  const autoPickBankAccRef = useRef(false)
  const autoPickBankAccSubRef = useRef(false)

  useEffect(() => {
    if (!autoPickBankAccRef.current) return
    const items = bankAccountsState.data?.items
    if (!items) return
    autoPickBankAccRef.current = false
    setDraft((prev) => (prev.bank_account ? prev : { ...prev, bank_account: items[0]?.value ?? '' }))
  }, [bankAccountsState.data])

  useEffect(() => {
    if (!autoPickBankAccSubRef.current) return
    const items = bankAccountsSubState.data?.items
    if (!items) return
    autoPickBankAccSubRef.current = false
    setDraft((prev) =>
      prev.bank_account_sub ? prev : { ...prev, bank_account_sub: items[0]?.value ?? '' },
    )
  }, [bankAccountsSubState.data])

  // Invoice Information snapshot — Access DLookUp invoice_date / total_payment /
  // vat_amount theo invoice_no (RO fields under "Invoice Information").
  useEffect(() => {
    if (!open) return
    const inv = draft.invoice_no.trim()
    if (!inv) {
      setInvoiceInfo({ date: '', amount: '', vat: '' })
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const info = (await fetchInvoiceInfo({ invoiceNo: inv }).unwrap()) as Record<string, unknown>
        if (cancelled) return
        setInvoiceInfo({
          date: String(info.invoice_date ?? '').slice(0, 10),
          amount: formatStandard(info.invoice_amount as number | string | null),
          vat: formatStandard(info.invoice_vat_amount as number | string | null),
        })
      } catch {
        if (!cancelled) setInvoiceInfo({ date: '', amount: '', vat: '' })
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.invoice_no])

  // Invoice Amount (after tax) — Access `=CDbl(cleanNumber(amount)) + CDbl(cleanNumber(vat))`.
  const invoiceAfterTax = useMemo(() => {
    const a = parseNumeric(invoiceInfo.amount)
    const v = parseNumeric(invoiceInfo.vat)
    if (a == null && v == null) return ''
    return formatStandard((a ?? 0) + (v ?? 0))
  }, [invoiceInfo])

  // -------------------------------------------------------------------------
  // Cascade: contract change — Access p_contract_AfterUpdate /
  // consignment_contract_AfterUpdate (full sequence, xem doc comment đầu file).
  // -------------------------------------------------------------------------
  const applyContractInfo = async (contractNo: string, npe: boolean) => {
    if (!contractNo) {
      // Access p_contract/consignment_AfterUpdate nhánh "cả hai HĐ rỗng": reset mọi
      // field phụ thuộc contract về "" rồi Check_Amount (fill 0.00). KHÔNG đụng
      // quantity (giữ input user như Check_Amount recalc bình thường).
      setContractSnap(EMPTY_SNAP)
      setProvSnap(null)
      setDraft((prev) =>
        recalcDraft(
          {
            ...prev,
            supplier: '',
            shipper_code: '',
            contract_ref_sub: '',
            price_sub: '',
            contract_price_unit: '',
            vat_txt: '',
            npe_fix_no: '',
            bank: '',
            bank_account: '',
            bank_sub: '',
            bank_account_sub: '',
            invoice_no: '',
            payment_type: '',
            price_unit: '',
            price: '',
            xntt_no: '',
          },
          EMPTY_SNAP,
          null,
        ),
      )
      return
    }
    let snap = EMPTY_SNAP
    try {
      const info = (await fetchContractInfo({ contractNo }).unwrap()) as Record<string, unknown>
      snap = buildSnap(info, npe)
      setContractSnap(snap)
      const cpCode = String(info.cp_code ?? '')
      const supplierName = String(info.supplier_name ?? '')
      const priceUnit = String(info.price_unit ?? '')
      setProvSnap(null) // truck giữ nguyên nhưng contract đổi → snapshot cũ vô hiệu
      setDraft((prev) => {
        let next: DraftState = {
          ...prev,
          supplier: cpCode ? `${cpCode} : ${supplierName}` : supplierName,
          // Access consignment branch clears the sub snapshot; P branch fills it
          // from the p_contract combo columns (contract_sub/shipper/price_sub).
          shipper_code: npe ? '' : String(info.shipper_code ?? ''),
          contract_ref_sub: npe ? '' : String(info.contract_ref_sub ?? ''),
          price_sub: npe ? '' : formatStandard(info.price_sub as number | string | null),
          contract_price_unit: npe ? '' : priceUnit,
          vat_txt: npe ? '' : String(info.vat_txt ?? ''),
          npe_fix_no: npe ? '' : String(info.npe_fix_no ?? ''),
          // Access: NPE forces VND/KG + Advanced (disabled); P clears payment_type
          // + lấy PriceUnit contract (fixation → VND/KG).
          price_unit: npe ? 'VND/KG' : snap.hasFixation ? 'VND/KG' : priceUnit,
          payment_type: npe ? 'Advanced' : '',
          bank: '',
          bank_account: '',
          bank_sub: '',
          bank_account_sub: '',
          // Access p_contract/consignment_AfterUpdate: Me.invoice_no = "" khi đổi HĐ.
          invoice_no: '',
          truck_id: npe ? '' : prev.truck_id,
        }
        if (npe) {
          // consignment_contract_AfterUpdate: price cleared.
          next.price = ''
        } else {
          // p_contract_AfterUpdate: quantity = pending; price theo contract type
          // (Outright → outright_price + clear futures/fx; else avg_price2);
          // fixation override → price_after_interest.
          next.quantity = formatStandard(snap.pending ?? 0)
          if (snap.ctype === 'Outright') {
            next.futures = ''
            next.requested_fx = ''
            next.price = formatStandard(snap.outrightPrice ?? 0)
          } else {
            next.price = formatStandard(snap.avgPrice2 ?? 0)
          }
          if (snap.hasFixation) next.price = formatStandard(snap.priceAfterInterest ?? 0)
        }
        // Access: mọi nhánh AfterUpdate kết thúc bằng Check_Amount.
        next = recalcDraft(next, snap, null)
        return next
      })
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to load contract info'))
    }
    // Auto-suggest xntt_no on contract pick (New / Copy only). Access p_contract &
    // consignment_AfterUpdate DÙNG CHUNG logic: nếu v_cs_fixation_p (p_contract_no =
    // HĐ hiện tại) có allocated_amount <> 0 → suggest theo contract_no fixation, else
    // theo chính HĐ. Áp cho CẢ P lẫn NPE (không gate theo npe).
    if (mode !== 'edit') {
      const suggestKey =
        snap.fixSuggestContract && (snap.fixAllocated ?? 0) !== 0
          ? snap.fixSuggestContract
          : contractNo
      try {
        const sug = await suggestXntt({ contractNo: suggestKey }).unwrap()
        setDraft((prev) => ({ ...prev, xntt_no: sug.suggestedNo }))
      } catch {
        /* ignore */
      }
    }
  }

  const handlePickPContract = (value: string) => {
    setDraft((prev) => ({ ...prev, p_contract: value, consignment_contract: '' }))
    void applyContractInfo(value, false)
  }

  const handlePickNpeContract = (value: string) => {
    setDraft((prev) => ({ ...prev, consignment_contract: value, p_contract: '' }))
    void applyContractInfo(value, true)
  }

  // payment_type_AfterUpdate — reprice + Check_Amount (P mode; NPE bị disable).
  const handlePickPaymentType = (value: string) => {
    setDraft((prev) => {
      let next: DraftState = { ...prev, payment_type: value }
      const snap = contractSnap
      if (prev.p_contract) {
        if (snap.hasFixation) {
          next.price = formatStandard(snap.priceAfterInterest ?? 0)
          next.price_unit = 'VND/KG'
        } else if (value === 'Outright' || value === 'Advanced') {
          next.price = formatStandard(
            (snap.ctype === 'Outright' ? snap.outrightPrice : snap.ptbfDiffs) ?? 0,
          )
          next.price_unit = prev.contract_price_unit
        } else if (value === 'Fixation') {
          next.price = formatStandard((snap.avgFpRealPaydata ?? 0) + (snap.ptbfDiffs ?? 0))
          next.price_unit = snap.avgFpPaydata === snap.avgFpRealPaydata ? 'USD/MT' : 'CTS/LB'
        } else if (value === 'Provisional') {
          next.price = formatStandard(
            ((snap.avgFpRealPaydataProvi ?? 0) + (snap.ptbfDiffs ?? 0)) *
              (snap.proviMarketRate ?? 0),
          )
          next.price_unit = snap.avgFpPaydata === snap.avgFpRealPaydata ? 'USD/MT' : 'CTS/LB'
        }
      }
      next = recalcDraft(next, snap, provSnap)
      return next
    })
  }

  // request_price_unit_AfterUpdate — Check_Amount.
  const handlePickPriceUnit = (value: string) => {
    setDraft((prev) => recalcDraft({ ...prev, price_unit: value }, contractSnap, provSnap))
  }

  // bank/bank_sub AfterUpdate — clear account, reload options (effect) rồi
  // auto-select account đầu tiên khi options về.
  const handlePickBank = (value: string) => {
    autoPickBankAccRef.current = true
    setDraft((prev) => ({ ...prev, bank: value, bank_account: '' }))
  }
  const handlePickBankSub = (value: string) => {
    autoPickBankAccSubRef.current = true
    setDraft((prev) => ({ ...prev, bank_sub: value, bank_account_sub: '' }))
  }

  // -------------------------------------------------------------------------
  // Field setters. Access AfterUpdate chỉ fire khi control dirty → track qua
  // dirtyRef; blur format Standard rồi mới chạy side-effect nếu có sửa.
  // -------------------------------------------------------------------------
  const dirtyRef = useRef<Set<keyof DraftState>>(new Set())

  const set = <K extends keyof DraftState>(key: K, value: string) => {
    dirtyRef.current.add(key)
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  const handleNumericBlur = (key: keyof DraftState) => () => {
    setDraft((prev) => {
      const formatted = formatStandard(prev[key])
      if (formatted === prev[key]) return prev
      return { ...prev, [key]: formatted }
    })
  }

  /** price/quantity/requested_fx/advanced AfterUpdate → Check_Amount. */
  const handleCalcBlur = (key: keyof DraftState) => () => {
    const dirty = dirtyRef.current.has(key)
    dirtyRef.current.delete(key)
    if (!dirty) {
      handleNumericBlur(key)()
      return
    }
    setDraft((prev) =>
      recalcDraft({ ...prev, [key]: formatStandard(prev[key]) || prev[key] }, contractSnap, provSnap),
    )
  }

  /** requested_usd/vnd AfterUpdate → vat_amount = Round(requested * vat_rate). */
  const handleVatBlur = (key: 'requested_usd' | 'requested_vnd') => () => {
    const dirty = dirtyRef.current.has(key)
    dirtyRef.current.delete(key)
    handleNumericBlur(key)()
    if (!dirty) return
    setDraft((prev) => {
      const vatRate = prev.p_contract ? contractSnap.vatRate : 0
      const v = excelRound(num0(prev[key]) * vatRate)
      return key === 'requested_usd'
        ? { ...prev, vat_amount_usd: formatUsd3(v) }
        : { ...prev, vat_amount_vnd: formatStandard(v) }
    })
  }

  // -------------------------------------------------------------------------
  // Validation (Access btnSave_Click error matrix, English copy).
  // -------------------------------------------------------------------------
  const cn = activeContract(draft)
  const errors = useMemo(() => {
    const e: string[] = []
    if (!draft.payment_date) e.push(npeMode ? 'Please choose Advanced Date!' : 'Please choose Payment Date!')
    if (!draft.p_contract && !draft.consignment_contract)
      e.push('Please choose P-Contract or Consignment!')
    if (draft.p_contract && draft.consignment_contract)
      e.push('Please choose only P-Contract or Consignment!')
    if (!draft.payment_type) e.push('Please choose Payment Type!')
    // Access NPE errCheck: XNTT No (9) đứng TRƯỚC Bank (3). P không check xntt ở đây.
    if (npeMode && !draft.xntt_no) e.push('Please type XNTT No!')
    if (!draft.bank) e.push('Please choose Bank!')
    if (parseNumeric(draft.price) == null) e.push('Please type Price!')
    if (parseNumeric(draft.quantity) == null) e.push('Please type Quantity!')
    if (draft.p_contract && draft.payment_type === 'Fixation' && (contractSnap.lotFixed ?? 0) === 0)
      e.push('This contract has not been fixed. Please choose other payment type!')
    if (npeMode) {
      // Access errCheck 6: Quantity <= to_be_advance (+ quantity cũ khi Edit).
      const quota = (contractSnap.toBeAdvance ?? 0) + (isEdit ? originalQty : 0)
      if ((parseNumeric(draft.quantity) ?? 0) > quota)
        e.push('Please type Quantity <= To Be Advanced!')
      if (parseNumeric(draft.interest_rate) == null) e.push('Please type Interest Rate!')
      if (!draft.fixation_deadline) e.push('Please type Fixation Deadline!')
    }
    return e
  }, [draft, npeMode, contractSnap, isEdit, originalQty])

  const canSave = canEdit && !saving && !hydrating && errors.length === 0

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  const savingRef = useRef(false)
  const handleSave = async () => {
    if (!canSave || savingRef.current) return
    savingRef.current = true
    try {
      // NPE duplicate xntt_no guard (New/Copy only — Access errCheck = 10).
      if (npeMode && mode !== 'edit' && draft.xntt_no) {
        try {
          const chk = await checkXntt({ xnttNo: draft.xntt_no }).unwrap()
          if (chk.exists) {
            snack.warning('XNTT No is existed!')
            savingRef.current = false
            return
          }
        } catch {
          /* if the check fails, defer to BE unique constraint */
        }
      }

      const body: PayDataWrite = {
        payment_date: draft.payment_date || null,
        contract_no: cn || null,
        xntt_no: draft.xntt_no || null,
        truck_id: draft.truck_id || null,
        payment_type: draft.payment_type || null,
        bank: draft.bank || null,
        bank_account: draft.bank_account || null,
        bank_sub: draft.bank_sub || null,
        bank_account_sub: draft.bank_account_sub || null,
        quantity: parseNumeric(draft.quantity),
        price: parseNumeric(draft.price),
        price_unit: draft.price_unit || null,
        futures: parseNumeric(draft.futures),
        lot: parseNumeric(draft.lot),
        advanced: parseNumeric(draft.advanced),
        requested_usd: parseNumeric(draft.requested_usd),
        requested_fx: parseNumeric(draft.requested_fx),
        requested_vnd: parseNumeric(draft.requested_vnd),
        paid_usd: parseNumeric(draft.paid_usd),
        fx: parseNumeric(draft.fx),
        paid_vnd: parseNumeric(draft.paid_vnd),
        vat_amount_usd: parseNumeric(draft.vat_amount_usd),
        vat_amount_vnd: parseNumeric(draft.vat_amount_vnd),
        interest_rate: parseNumeric(draft.interest_rate),
        fixation_deadline: draft.fixation_deadline || null,
        invoice_no: draft.invoice_no || null,
        note: draft.note || null,
        is_advanced: npeMode ? 1 : 0,
        advanced_sub: parseNumeric(draft.advanced_sub),
        requested_usd_sub: parseNumeric(draft.requested_usd_sub),
        requested_vnd_sub: parseNumeric(draft.requested_vnd_sub),
        invoice_no_sub: draft.invoice_no_sub || null,
      }

      if (mode === 'edit' && paydataId != null) {
        body.paydata_p_id = paydataId
        await updatePayData({ id: paydataId, body }).unwrap()
        snack.success('Successfully Updated')
      } else {
        await createPayData(body).unwrap()
        snack.success('Successfully Added')
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save pay data'))
    } finally {
      savingRef.current = false
    }
  }

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const titleText =
    mode === 'new' ? 'Pay Data - New' : mode === 'copy' ? 'Pay Data - Copy' : 'Pay Data - Edit'

  /** Flat Autocomplete for a {value,label} lookup; injects the saved value. */
  const renderCombo = (params: {
    label: string
    value: string
    options: Opt[]
    onPick: (v: string) => void
    sx?: SxProps<Theme>
    disabled?: boolean
    loading?: boolean
    required?: boolean
    error?: boolean
    tabIndex?: number
    onInput?: (q: string) => void
    /** mark required field touched on blur → defer error border (skill §Required) */
    onBlur?: () => void
    /** collapse "<code> — <label>" when code === label (e.g. price_unit) */
    collapseEqual?: boolean
  }) => {
    const { label, value, options, onPick, sx, disabled, loading, required, error, tabIndex, onInput, onBlur, collapseEqual } =
      params
    const sel =
      options.find((o) => o.value === value) ?? (value ? { value, label: value } : null)
    const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
    return (
      <Autocomplete
        size="small"
        sx={sx}
        options={merged}
        value={sel}
        disabled={disabled}
        onChange={(_e, v) => onPick(v?.value ?? '')}
        onBlur={onBlur}
        onInputChange={onInput ? (_e, v, reason) => reason === 'input' && onInput(v) : undefined}
        getOptionLabel={(o) => {
          if (!o) return ''
          const code = String(o.value ?? '')
          const lab = String(o.label ?? '')
          if (!lab || lab === code) return code
          if (collapseEqual && code === lab) return code
          return `${code} — ${lab}`
        }}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(p) => (
          <TextField
            {...p}
            label={label}
            required={required}
            error={error}
            inputProps={{ ...p.inputProps, ...(tabIndex != null ? { tabIndex } : {}) }}
            InputProps={{
              ...p.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={14} color="inherit" /> : null}
                  {p.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    )
  }

  const numField = (
    key: keyof DraftState,
    label: string,
    sx?: SxProps<Theme>,
    opts?: {
      ro?: boolean
      tabIndex?: number
      required?: boolean
      error?: boolean
      /** AfterUpdate variant: 'calc' → Check_Amount; 'vatUsd'/'vatVnd' → VAT recompute. */
      blur?: 'calc' | 'vatUsd' | 'vatVnd'
    },
  ) => (
    <TextField
      label={label}
      required={opts?.required}
      size="small"
      fullWidth
      type="text"
      value={draft[key]}
      onChange={(e) => set(key, e.target.value)}
      onBlur={
        opts?.ro
          ? undefined
          : () => {
              touch(key as string)
              if (opts?.blur === 'calc') handleCalcBlur(key)()
              else if (opts?.blur === 'vatUsd') handleVatBlur('requested_usd')()
              else if (opts?.blur === 'vatVnd') handleVatBlur('requested_vnd')()
              else handleNumericBlur(key)()
            }
      }
      error={opts?.error}
      disabled={!opts?.ro && !canEdit}
      inputProps={{
        style: { textAlign: 'right' },
        inputMode: 'decimal',
        tabIndex: opts?.ro ? -1 : opts?.tabIndex,
      }}
      InputProps={opts?.ro ? { readOnly: true } : undefined}
      InputLabelProps={opts?.ro ? { shrink: true } : undefined}
      sx={[numSx, ...(opts?.ro ? [roSx] : []), ...(sx ? [sx] : [])] as SxProps<Theme>}
    />
  )

  const roField = (label: string, value: string, sx?: SxProps<Theme>) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={value}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ tabIndex: -1 }}
      sx={[roSx, ...(sx ? [sx] : [])] as SxProps<Theme>}
    />
  )

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title={titleText} sx={dialogTitleTextSx}>
          {titleText}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {hydrating ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
          <>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for Pay Data.
          </Alert>
        )}
        {errors.length > 0 && canEdit && (
          <Alert severity="warning" sx={{ mb: 1.5, py: 0.25 }}>
            {errors[0]}
          </Alert>
        )}

        {/* 2-col grid với explicit gridRow bám Access Top (xem header comment):
            General Information (r1-6, gồm xntt_no/payment_type ở r6; supplier span
            r3-4) / Request (r7-13) / Actual Payment (r14-16) / For NPE + Invoice
            Information (r17-20, mỗi ô Invoice split đôi trong col2) / Sub Information
            (r21-26) / Note (r27). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
          }}
        >
          {/* Top block — Access has no section header above these fields. */}

          {/* r1 — paydata_p_id (RO) | payment_date */}
          {roField('PayData ID', isEdit && paydataId != null ? String(paydataId) : '', cell(1, 1))}
          <Box sx={cell(2, 1)}>
            <DatePicker
              label="Date"
              format="DD-MMM-YYYY"
              disabled={!canEdit}
              value={draft.payment_date ? dayjs(draft.payment_date) : null}
              onChange={(d) => set('payment_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: !!touched.payment_date && !draft.payment_date,
                  onBlur: () => touch('payment_date'),
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: 1 },
                },
              }}
            />
          </Box>

          {/* r2 — p_contract | consignment_contract */}
          {renderCombo({
            label: 'Contract - Purchase',
            value: draft.p_contract,
            options: pContractOptions,
            onPick: handlePickPContract,
            disabled: !canEdit || isEdit,
            loading: contractsState.isFetching,
            onInput: setPQuery,
            tabIndex: 2,
            sx: cell(1, 2),
          })}
          {renderCombo({
            label: 'Contract - NPE',
            value: draft.consignment_contract,
            options: npeContractOptions,
            onPick: handlePickNpeContract,
            disabled: !canEdit || isEdit,
            loading: contractsState.isFetching,
            onInput: setNpeQuery,
            tabIndex: 3,
            sx: cell(2, 2),
          })}

          {/* r3 col1 — Contract Price Unit | Contract Type (split đôi trong col1) */}
          <Box
            sx={{
              ...cell(1, 3),
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 1.5,
              rowGap: 1.25,
            }}
          >
            {roField('Contract - Price Unit', draft.contract_price_unit)}
            {roField('Contract - Contract Type', contractSnap.ctype)}
          </Box>

          {/* r3-5 — [vat_txt | npe_fix_no] (r4) + truck_id (r5) | supplier (RO tall,
              span r3-4 = 2 dòng). Access: consignment chọn → truck_id.Visible = False. */}
          {!npeMode &&
            renderCombo({
              label: 'Truck ID',
              value: draft.truck_id,
              options: truckIdOptions,
              onPick: (v) => set('truck_id', v),
              disabled: !canEdit,
              loading: truckIdsState.isFetching,
              tabIndex: 4,
              sx: cell(1, 5),
            })}
          <TextField
            label="Contract - Supplier"
            size="small"
            fullWidth
            multiline
            value={draft.supplier}
            InputProps={{ readOnly: true, sx: { height: '100%', alignItems: 'flex-start' } }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: -1 }}
            sx={[
              roSx,
              {
                gridColumn: { xs: '1', sm: '2' },
                gridRow: { sm: '3 / span 2' },
                height: '100%',
              },
            ]}
          />
          {/* r4 col1 — Applicable VAT? | NPE Fix No (split đôi trong col1) */}
          <Box
            sx={{
              ...cell(1, 4),
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 1.5,
              rowGap: 1.25,
            }}
          >
            {roField('Applicable VAT?', draft.vat_txt)}
            {roField('NPE Fix.No', draft.npe_fix_no)}
          </Box>

          {/* r6 — xntt_no | payment_type (Access Top 2040 → last General Information
              row, ABOVE the Request header which starts at Top 2316) */}
          <TextField
            label="XNTT No."
            required={npeMode}
            size="small"
            fullWidth
            value={draft.xntt_no}
            onChange={(e) => set('xntt_no', e.target.value)}
            onBlur={() => touch('xntt_no')}
            disabled={!canEdit}
            error={npeMode && !!touched.xntt_no && !draft.xntt_no}
            inputProps={{ tabIndex: 5 }}
            sx={cell(1, 6)}
          />
          {renderCombo({
            label: 'Payment Type',
            value: draft.payment_type,
            options: paymentTypeOptions,
            onPick: handlePickPaymentType,
            disabled: !canEdit || npeMode, // NPE forces Advanced
            required: true,
            error: !!touched.payment_type && !draft.payment_type,
            onBlur: () => touch('payment_type'),
            tabIndex: 6,
            sx: cell(2, 6),
          })}

          {/* ── Section: Request (Access Label702, Top 2316) ── */}
          <Typography variant="caption" sx={sectionSx(7)}>
            Request
          </Typography>

          {/* r8 — bank | bank_account */}
          {renderCombo({
            label: 'Bank',
            value: draft.bank,
            options: bankOptions,
            onPick: handlePickBank,
            disabled: !canEdit,
            required: true,
            error: !!touched.bank && !draft.bank,
            onBlur: () => touch('bank'),
            loading: banksState.isFetching,
            tabIndex: 7,
            sx: cell(1, 8),
          })}
          {renderCombo({
            label: 'Bank Account No.',
            value: draft.bank_account,
            options: bankAccountOptions,
            onPick: (v) => set('bank_account', v),
            disabled: !canEdit,
            loading: bankAccountsState.isFetching,
            tabIndex: 8,
            sx: cell(2, 8),
          })}

          {/* r9 — price | quantity */}
          {numField('price', 'Price', cell(1, 9), {
            tabIndex: 9,
            required: true,
            error: !!touched.price && parseNumeric(draft.price) == null,
            blur: 'calc',
          })}
          {numField('quantity', 'Quantity (kg)', cell(2, 9), {
            tabIndex: 10,
            required: true,
            error: !!touched.quantity && parseNumeric(draft.quantity) == null,
            blur: 'calc',
          })}

          {/* r10 — request price unit (combo) | futures */}
          {renderCombo({
            label: 'PriceUnit',
            value: draft.price_unit,
            options: priceUnitOptions,
            onPick: handlePickPriceUnit,
            disabled: !canEdit,
            collapseEqual: true,
            tabIndex: 11,
            sx: cell(1, 10),
          })}
          {numField('futures', 'Futures', cell(2, 10), { tabIndex: 12 })}

          {/* r11 — lot | advanced */}
          {numField('lot', 'Lot', cell(1, 11), { tabIndex: 13 })}
          {numField('advanced', 'Advanced', cell(2, 11), { tabIndex: 14, blur: 'calc' })}

          {/* r12 — requested_usd | requested_vnd */}
          {numField('requested_usd', 'Request USD', cell(1, 12), { tabIndex: 15, blur: 'vatUsd' })}
          {numField('requested_vnd', 'Requested VND', cell(2, 12), { tabIndex: 16, blur: 'vatVnd' })}

          {/* r13 — requested_fx | vat_amount_vnd (Access: vat_amount_usd hidden) */}
          {numField('requested_fx', 'Request FX', cell(1, 13), { tabIndex: 17, blur: 'calc' })}
          {numField('vat_amount_vnd', 'VAT Amount VND', cell(2, 13), { tabIndex: 18 })}

          {/* ── Section: Actual Payment ── */}
          <Typography variant="caption" sx={sectionSx(14)}>
            Actual Payment
          </Typography>

          {/* r15 — paid_usd | paid_fx */}
          {numField('paid_usd', 'Paid USD', cell(1, 15), { tabIndex: 19 })}
          {numField('fx', 'Paid FX', cell(2, 15), { tabIndex: 20 })}

          {/* r16 — paid_vnd | — */}
          {numField('paid_vnd', 'Paid VND', cell(1, 16), { tabIndex: 21 })}

          {/* ── Section headers side by side (Access: For NPE @left, Invoice @right, same band) ── */}
          <Typography
            variant="caption"
            sx={{
              ...sectionHeaderSx,
              color: 'text.secondary',
              gridColumn: { xs: '1', sm: '1' },
              gridRow: { sm: '17' },
              mt: 0.5,
            }}
          >
            For NPE
          </Typography>
          <Box
            sx={{
              gridColumn: { xs: '1', sm: '2' },
              gridRow: { sm: '17' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', ...sectionHeaderSx }}>
              Invoice Information
            </Typography>
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => setAddInvoiceOpen(true)}
              disabled={!canEdit}
              tabIndex={22}
              sx={{ py: 0, minHeight: 24, fontSize: '0.7rem' }}
            >
              Add Invoice
            </Button>
          </Box>

          {/* r18 — interest_rate | invoice_no */}
          {numField('interest_rate', 'Interest Rate', cell(1, 18), {
            tabIndex: 24,
            required: npeMode,
            error: npeMode && !!touched.interest_rate && parseNumeric(draft.interest_rate) == null,
          })}
          {renderCombo({
            label: 'Invoice',
            value: draft.invoice_no,
            options: invoiceNoOptions,
            onPick: (v) => set('invoice_no', v),
            disabled: !canEdit,
            loading: invoiceNosState.isFetching,
            tabIndex: 23,
            sx: cell(2, 18),
          })}

          {/* r19 — fixation_deadline | invoice_date (RO) */}
          <Box sx={cell(1, 19)}>
            <DatePicker
              label="Fixation Deadline"
              format="DD-MMM-YYYY"
              disabled={!canEdit}
              value={draft.fixation_deadline ? dayjs(draft.fixation_deadline) : null}
              onChange={(d) =>
                set('fixation_deadline', d && d.isValid() ? d.format('YYYY-MM-DD') : '')
              }
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: npeMode,
                  error: npeMode && !!touched.fixation_deadline && !draft.fixation_deadline,
                  onBlur: () => touch('fixation_deadline'),
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: 25 },
                },
              }}
            />
          </Box>
          {/* r19 col2 — Invoice Date | VAT Amount (split đôi trong width cũ của col2) */}
          <Box
            sx={{
              ...cell(2, 19),
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 1.5,
              rowGap: 1.25,
            }}
          >
            {roField(
              'Invoice Date',
              invoiceInfo.date ? dayjs(invoiceInfo.date).format('DD-MMM-YYYY') : '',
            )}
            <TextField
              label="VAT Amount"
              size="small"
              fullWidth
              value={invoiceInfo.vat}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: -1, style: { textAlign: 'right' } }}
              sx={[roSx, numSx] as SxProps<Theme>}
            />
          </Box>

          {/* r20 col2 — Invoice Amount | Invoice Amount (after tax) (split đôi) */}
          <Box
            sx={{
              ...cell(2, 20),
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 1.5,
              rowGap: 1.25,
            }}
          >
            <TextField
              label="Invoice Amount"
              size="small"
              fullWidth
              value={invoiceInfo.amount}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: -1, style: { textAlign: 'right' } }}
              sx={[roSx, numSx] as SxProps<Theme>}
            />
            <TextField
              label="Invoice Amount (after tax)"
              size="small"
              fullWidth
              value={invoiceAfterTax}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: -1, style: { textAlign: 'right' } }}
              sx={[roSx, numSx] as SxProps<Theme>}
            />
          </Box>

          {/* ── Section: Sub Information (dời lên -2 row do gộp Invoice Info còn 2 dòng) ── */}
          <Typography variant="caption" sx={sectionSx(21)}>
            Sub Information
          </Typography>

          {/* r22 — contract_ref_sub (RO) | price_sub (RO) */}
          {roField('Contract Ref Sub', draft.contract_ref_sub, cell(1, 22))}
          {numField('price_sub', 'Price Sub', cell(2, 22), { ro: true })}

          {/* r23 — shipper_code (RO) | — */}
          {roField('Shipper Code', draft.shipper_code, cell(1, 23))}

          {/* r24 — advanced_sub | requested_usd_sub */}
          {numField('advanced_sub', 'Advanced Sub', cell(1, 24), { tabIndex: 26 })}
          {numField('requested_usd_sub', 'Request USD Sub', cell(2, 24), { tabIndex: 27 })}

          {/* r25 — invoice_no_sub (combo) | requested_vnd_sub */}
          {renderCombo({
            label: 'Invoice No. Sub',
            value: draft.invoice_no_sub,
            options: invoiceNoOptions,
            onPick: (v) => set('invoice_no_sub', v),
            disabled: !canEdit,
            tabIndex: 28,
            sx: cell(1, 25),
          })}
          {numField('requested_vnd_sub', 'Request VND Sub', cell(2, 25), { tabIndex: 29 })}

          {/* r26 — bank_sub | bank_account_sub (Access: filter theo shipper_code) */}
          {renderCombo({
            label: 'Bank Sub',
            value: draft.bank_sub,
            options: bankSubOptions,
            onPick: handlePickBankSub,
            disabled: !canEdit,
            loading: banksSubState.isFetching,
            tabIndex: 30,
            sx: cell(1, 26),
          })}
          {renderCombo({
            label: 'Bank Account No. Sub',
            value: draft.bank_account_sub,
            options: bankAccountSubOptions,
            onPick: (v) => set('bank_account_sub', v),
            disabled: !canEdit,
            loading: bankAccountsSubState.isFetching,
            tabIndex: 31,
            sx: cell(2, 26),
          })}

          {/* r27 — note (full width, Access H=567 ≈ 2 rows) */}
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft.note}
            onChange={(e) => set('note', e.target.value)}
            disabled={!canEdit}
            inputProps={{ tabIndex: 32 }}
            sx={{ gridColumn: { xs: '1', sm: '1 / span 2' }, gridRow: { sm: '27' } }}
          />
        </Box>
          </>
        )}
      </DialogContent>

      {/* Access btnAddInvoice → frmProcurementQryInvoiceAdd (prefill p_contract). */}
      <ProcurementInvoiceFormDialog
        open={addInvoiceOpen}
        mode="new"
        invoiceId={null}
        canEdit={canEdit}
        initialContractNo={draft.p_contract || null}
        onClose={() => setAddInvoiceOpen(false)}
        onSaved={() => {
          setAddInvoiceOpen(false)
          const activeCn = activeContract(draft)
          if (activeCn)
            void fetchInvoiceNos({
              contractNo: activeCn,
              contractNo2: contractSnap.npeRef || undefined,
            })
        }}
      />
    </Dialog>
  )
}
